import { Button, cn, Tooltip } from '@heroui/react'
import { ArrowUp, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

interface TextPreviewProps {
  url: string
}

export default function TextPreview({ url }: TextPreviewProps) {
  const [content, setContent] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [fontSize, setFontSize] = useState<number>(16)
  const [showScrollToTop, setShowScrollToTop] = useState<boolean>(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Font size limits
  const MIN_FONT_SIZE = 10
  const MAX_FONT_SIZE = 32
  const DEFAULT_FONT_SIZE = 16

  // Load text content
  useEffect(() => {
    const fetchText = async () => {
      try {
        setError(null)

        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const text = await response.text()

        setContent(text)
        setIsLoading(false)
      } catch (err) {
        console.error('Text load error:', err)
        setError('Failed to load text file. The file may be corrupted or unavailable.')
        setIsLoading(false)
      }
    }

    if (url) {
      fetchText()
    }
  }, [url])

  // Font size controls
  const handleZoomIn = useCallback(() => {
    setFontSize(prev => Math.min(prev + 2, MAX_FONT_SIZE))
  }, [])

  const handleZoomOut = useCallback(() => {
    setFontSize(prev => Math.max(prev - 2, MIN_FONT_SIZE))
  }, [])

  const handleReset = useCallback(() => {
    setFontSize(DEFAULT_FONT_SIZE)
  }, [])

  // Scroll to top function
  const scrollToTop = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  // Handle scroll to show/hide scroll to top button
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const container = containerRef.current!
      const scrollTop = container.scrollTop

      setShowScrollToTop(scrollTop > 200)
    }, 80)
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '+' || (e.key === '=' && (e.ctrlKey || e.metaKey))) {
        e.preventDefault()
        handleZoomIn()
      }
      if (e.key === '-' || (e.key === '_' && (e.ctrlKey || e.metaKey))) {
        e.preventDefault()
        handleZoomOut()
      }
      if (e.key === '0' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        handleReset()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleZoomIn, handleZoomOut, handleReset])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
    }
  }, [])

  if (error) {
    return (
      <div className="max-w-xl text-center text-white">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/15 text-red-400">
          <svg fill="none" height="28" viewBox="0 0 24 24" width="28">
            <path
              d="M12 9v4m0 4h.01M4.5 19h15a1.5 1.5 0 0 0 1.3-2.25L13.3 4.75a1.5 1.5 0 0 0-2.6 0L3.2 16.75A1.5 1.5 0 0 0 4.5 19Z"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
            />
          </svg>
        </div>
        <h3 className="mb-2 text-xl font-semibold">Unable to preview text file</h3>
        <p className="text-neutral-200">{error}</p>
      </div>
    )
  }

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center px-8">
      {/* Text Container */}
      <div
        ref={containerRef}
        className={cn(
          'relative w-full flex-1 flex overflow-auto scroll-smooth preview-content',
          isLoading && 'items-center justify-center',
        )}
        style={{
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
        }}
        onScroll={handleScroll}
      >
        {isLoading && (
          <div className="text-center">
            <div className="mx-auto mb-4 h-14 w-14 animate-spin rounded-full border-b-2 border-neutral-300" />
            <p className="text-base font-medium text-neutral-100">Loading text file...</p>
          </div>
        )}

        {!isLoading && content && (
          <div className="w-full maxw-4xl mx-auto">
            <div className="bg-white w-full min-h-[92%] dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 shadow-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <pre
                className="whitespace-pre-wrap break-words font-mono leading-relaxed overflow-hidden"
                style={{ fontSize: `${fontSize}px` }}
              >
                {content}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <Tooltip content="Scroll to top" placement="left">
          <Button
            isIconOnly
            aria-label="Scroll to top"
            className="fixed bottom-24 right-8 bg-black/60 hover:bg-black/80 text-white/90 backdrop-blur-md shadow-lg border border-white/15"
            size="lg"
            variant="flat"
            onPress={scrollToTop}
          >
            <ArrowUp className="h-5 w-5" />
          </Button>
        </Tooltip>
      )}

      {/* Bottom Controls */}
      <div className="pointer-events-none absolute inset-x-0 bottom-[max(16px,env(safe-area-inset-bottom))] flex justify-center bottom-controls z-10">
        <div className="pointer-events-auto flex items-center gap-1 rounded-full border border-white/15 bg-black/60 px-2 py-1 backdrop-blur-md shadow-lg">
          {/* Zoom Controls */}
          <Tooltip content="Decrease text size" placement="top">
            <Button
              isIconOnly
              aria-label="Decrease text size"
              className="text-white/90 hover:bg-white/10"
              isDisabled={fontSize <= MIN_FONT_SIZE}
              size="sm"
              variant="light"
              onPress={handleZoomOut}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
          </Tooltip>

          <span className="mx-1 w-12 text-center text-xs text-white/90 tabular-nums">
            {fontSize}px
          </span>

          <Tooltip content="Increase text size" placement="top">
            <Button
              isIconOnly
              aria-label="Increase text size"
              className="text-white/90 hover:bg-white/10"
              isDisabled={fontSize >= MAX_FONT_SIZE}
              size="sm"
              variant="light"
              onPress={handleZoomIn}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </Tooltip>

          <div className="mx-1 h-4 w-px bg-white/20" />

          <Tooltip content="Reset text size" placement="top">
            <Button
              isIconOnly
              aria-label="Reset text size"
              className="text-white/90 hover:bg-white/10"
              size="sm"
              variant="light"
              onPress={handleReset}
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
